export interface SubmissionError {
	errorMessage: string;
}

const DEFAULT_FALLBACK = "An unexpected error occurred. Please try again later.";

/**
 * The API returns different error shapes per status code: FluentValidation failures (400),
 * ProblemDetails (500) and plain strings. This flattens all of them into one message list.
 */
const collectMessages = (error: unknown, messages: string[]): void => {
	if (error === null || error === undefined) {
		return;
	}

	if (Array.isArray(error)) {
		for (const item of error) {
			collectMessages(item, messages);
		}
		return;
	}

	if (typeof error === "string") {
		if (error.trim()) {
			messages.push(error.trim());
		}
		return;
	}

	if (error instanceof Error) {
		return;
	}

	if (typeof error !== "object") {
		return;
	}

	const record = error as Record<string, unknown>;

	// ValidationProblemDetails: { errors: { propertyName: string[] } }
	if (record["errors"] && typeof record["errors"] === "object") {
		collectMessages(Object.values(record["errors"] as Record<string, unknown>), messages);
		return;
	}

	const candidate = record["errorMessage"] ?? record["detail"] ?? record["title"] ?? record["message"];

	if (typeof candidate === "string" && candidate.trim()) {
		messages.push(candidate.trim());
	}
};

export const toSubmissionErrors = (error: unknown, fallback: string = DEFAULT_FALLBACK): SubmissionError[] => {
	const messages: string[] = [];
	collectMessages(error, messages);

	const unique = [...new Set(messages)];

	return (unique.length > 0 ? unique : [fallback]).map((errorMessage) => ({ errorMessage }));
};
